/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import clsx from "clsx";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

import styles from "../../styles/tinymce.module.css";

import { engageState } from "../../atoms/platform";
import { getItemNumberOnly, getGroupForTitleId } from "../../helpers/text";
import { textToHtmlTinyMCE } from "../../helpers/converter";
import { getNameByGroupId } from "../../helpers/arrays";
import { apiGetTopicPrintClean } from "../../lib/api/topics";
import { PRINT_HOST, ENV_MODE } from "../../helpers/printing";

import { XIcon } from "@heroicons/react/outline";
import { AiOutlinePrinter } from "react-icons/ai";
import {
  // IoAttachOutline,
  IoDocumentTextOutline,
  // IoDownload,
} from "react-icons/io5";

import EditorReadOnly from "../RichText/EditorReadOnly";
import CustomFieldPrint from "../Actions/Dark/CustomFieldPrint";
// import PDFViewer from "../Actions/PDFViewer";
// import FileDrop from "../Shared/DropDown/FileDrop";
import AdobeViewer from "../Pdf/AdobeViewer";
import EditorHTML from "../RichText/EditorHTML";
import TallyResults from "./TallyResults";

const AgendaTopicPlayerToc = ({ visible, topic, client }) => {
  const platform = useRecoilValue(engageState);
  const [resources, setResources] = useState([]);
  const [mobileFile, setMobileFile] = useState();
  const [localTopic, setLocalTopic] = useState();
  const [toc, setToc] = useState([]);
  const [activeToc, setActiveToc] = useState();
  const [viewToc, setViewToc] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        visible(false);
      }
    };

    if (visible) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible]);

  useEffect(() => {
    async function fetchTopic() {
      const result = await apiGetTopicPrintClean(topic);
      if (result) {
        setLocalTopic(result);
        let minutesContent;
        try {
          minutesContent = JSON.parse(result.minutesInfo)?.trim();
        } catch {
          minutesContent = result.minutesInfo?.trim();
        }
        const hasValidMinutes =
          minutesContent && !/^[\/\s]+$/.test(minutesContent);
        if (result.attachments?.length > 0) {
          const filteredArray = result.attachments.filter(
            (obj) =>
              obj.public === true && obj.fileType?.toLowerCase() === "pdf",
          );
          setResources(filteredArray);
          setMobileFile(filteredArray[0]);
          let tempToc = [];
          if (client === "federalwaywa") {
            tempToc.push({
              id: "bill",
              fileType: "bill",
              fileName: "Agenda Bill",
            });
          }
          if (result.hasDetails) {
            tempToc.push({
              id: "report",
              fileType: "report",
              fileName: "Report",
            });
          }
          if (result.meeting?.publishStatus === "minutes") {
            if (hasValidMinutes) {
              tempToc.push({
                id: "minutes",
                fileType: "minutes",
                fileName: "Minutes",
              });
            }
          }
          tempToc.push(...filteredArray);
          if (tempToc.length > 0) {
            setActiveToc(tempToc[0]);
          }
          setToc(tempToc);
        } else {
          let tempToc = [];
          if (client === "federalwaywa") {
            tempToc.push({
              id: "bill",
              fileType: "bill",
              fileName: "Agenda Bill",
            });
          }
          if (result.hasDetails) {
            tempToc.push({
              id: "report",
              fileType: "report",
              fileName: "Report",
            });
          }
          if (result.meeting?.publishStatus === "minutes") {
            if (hasValidMinutes) {
              tempToc.push({
                id: "minutes",
                fileType: "minutes",
                fileName: "Minutes",
              });
            }
          }
          if (tempToc.length > 0) {
            setActiveToc(tempToc[0]);
          }
          setToc(tempToc);
        }
      }
    }
    if (topic) {
      fetchTopic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic]);

  const handleFileChange = (value) => {
    setMobileFile(value);
  };

  const handleDownload = () => {
    axios({
      url: mobileFile?.fileUrl,
      method: "GET",
      responseType: "blob",
    })
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `${mobileFile.fileName}.pdf`); // Specify the desired filename and extension
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.error("Error downloading the file:", error);
      });
  };

  return (
    <div className="w-full bg-white">
      {topic && (
        <div className="h-full">
          {/* Header */}
          <div className="px-4 pt-4 border-b border-zinc-700">
            <div className="mt-4 flex w-full justify-between">
              <div
                className={clsx(
                  "flex items-center justify-center transform duration-200 cursor-pointer hover:text-blue-500 pb-1 col-span-2",
                )}
                onClick={() => setViewToc(!viewToc)}
              >
                <IoDocumentTextOutline className="h-6 w-6 mr-1.5" />
                <p className="text-sm 2xl:text-base">Table of Contents</p>
              </div>

              <div className="flex items-start justify-end space-x-3">
                {localTopic?.hasDetails && (
                  <div className="space-y-1 2xl:text-lg flex font-medium text-black">
                    {localTopic?.editorType === 1 ? (
                      <a
                        href={`${process.env.NEXT_PUBLIC_BASE_URL}/${client}/print/action/${topic}?print=true`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center hover:text-blue-500 transition duration-200"
                      >
                        <AiOutlinePrinter className="h-4 w-4 2xl:h-6 2xl:w-6 mr-4" />
                      </a>
                    ) : (
                      <a
                        href={`${PRINT_HOST}/v6/${client}/${topic}?mode=${ENV_MODE}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center hover:text-blue-500 transition duration-200"
                      >
                        <AiOutlinePrinter className="h-4 w-4 2xl:h-6 2xl:w-6 mr-4" />
                      </a>
                    )}
                  </div>
                )}

                <div className="h-7 flex items-center">
                  <button
                    type="button"
                    className="rounded-md text-black hover:text-delete-btn focus:outline-none cursor-pointer hover:rotate-180 transform duration-150"
                    onClick={() => {
                      visible(false);
                    }}
                  >
                    <span className="sr-only">Close panel</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
            <AnimatePresence initial={false}>
              {viewToc && (
                <motion.nav
                  key="toc-nav"
                  aria-label="Table of Contents"
                  className="w-full"
                  initial={{ height: 0, opacity: 0, y: -10 }}
                  animate={{ height: "auto", opacity: 1, y: 0 }}
                  exit={{ height: 0, opacity: 0, y: -10 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                >
                  <motion.ul
                    aria-labelledby="toc-heading"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {toc.map((file, index) => (
                      <li
                        key={file.id || file._id || index}
                        className="mb-2 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => {
                            setActiveToc(file);
                            setViewToc(false);
                          }}
                          className={clsx(
                            file.id.toString() === activeToc?.id
                              ? "bg-blue-400 text-white"
                              : "bg-gray-100 text-black",
                            "w-full text-left px-2 py-1.5 rounded",
                            "hover:bg-gray-200 hover:text-black focus-visible:ring-2 focus-visible:ring-blue-500",
                            "transition font-medium text-sm 2xl:text-base",
                            "block overflow-hidden text-ellipsis break-words",
                            "line-clamp-2 2xl:line-clamp-1",
                          )}
                        >
                          {file.fileName}
                        </button>
                      </li>
                    ))}
                  </motion.ul>
                </motion.nav>
              )}
            </AnimatePresence>
            {/* {viewToc && (
              <nav aria-label="Table of Contents" className="w-full">
                <ul aria-labelledby="toc-heading">
                  {toc.map((file, index) => (
                    <li
                      key={file.id || file._id || index}
                      className="mb-2 overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setActiveToc(file);
                          setViewToc(false);
                        }} // your action here
                        className={clsx(
                          file.id.toString() === activeToc?.id
                            ? "bg-blue-400 text-white"
                            : "bg-gray-100 text-black",
                          "w-full text-left px-2 py-1.5 rounded",
                          "hover:bg-gray-200 hover:text-black focus-visible:ring-2 focus-visible:ring-blue-500",
                          "transition font-medium text-sm 2xl:text-base",
                          // ↓ ensure proper truncation behavior
                          "block overflow-hidden text-ellipsis break-words",
                          "line-clamp-2 2xl:line-clamp-1"
                        )}
                      >
                        {file.fileName}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )} */}
          </div>
          <div className="h-full">
            {/* Menu Selection */}

            {/* Details */}
            <div className="h-screen flex flex-col overflow-hidden">
              {/* <div className="h-screen overflow-hidden"> */}
              {/* Document */}
              {activeToc?.fileType === "report" && platform && localTopic && (
                <>
                  {localTopic.editorType === 1 ? (
                    <div className="grid grid-cols-6 mx-4 2xl:mx-6 overflow-y-auto pb-md">
                      <div className="relative col-span-2 overflow-hidden min-h-[7em]">
                        <img
                          className="absolute object-contain h-full w-full p-4"
                          src={platform.logo}
                          alt=""
                        />
                      </div>
                      <div className="col-span-4 border-b border-black mt-2">
                        <div className="h-full w-full overflow-hidden grid grid-rows-2">
                          <div className="flex justify-end items-end font-semibold text-xl">
                            <h3 className="">{platform.clientName}</h3>
                          </div>
                          <div className="flex justify-end items-center font-semibold text-lg">
                            <h3 className="text-right">
                              {localTopic.department?.name}{" "}
                              {platform.memoCalled}
                            </h3>
                          </div>
                        </div>
                      </div>
                      {platform?.fieldPreparedBy && (
                        <div className="col-span-6 w-full flex justify-end">
                          <p className="text-[12px]">
                            Prepared By: {localTopic.modifiedBy?.name}
                          </p>
                        </div>
                      )}
                      {/* Agenda Title */}
                      <div className="mt-4 col-span-6 border-b border-gray-800 text-[12px]">
                        <p className="font-semibold 2xl:text-lg">
                          {localTopic.title}
                        </p>
                        <p className="whitespace-pre-line">
                          {localTopic.topicBlurb}
                        </p>
                        {localTopic.subTopics?.length > 0 &&
                          localTopic.subTopics.map((sub, sIdx) => {
                            return (
                              <p key={`sub-${sIdx}`} className="ml-8 py-0.5">
                                &#8212; {sub}
                              </p>
                            );
                          })}
                      </div>
                      {/* General Info */}
                      <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                        <div className="bg-gray-200 border-b border-gray-800 mb-1">
                          <p className="text-center font-medium">Meeting</p>
                        </div>
                        <div className="">
                          <p className="text-center">
                            {localTopic.meeting?.scheduleDate}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                        <div className="bg-gray-200 border-b border-gray-800 mb-1">
                          <p className="text-center font-medium">
                            Agenda Group
                          </p>
                        </div>
                        <div className="flex items-center justify-evenly">
                          <p className="">
                            {getNameByGroupId(
                              localTopic.agendaGroup.id,
                              localTopic.meeting?.groupSubheadings,
                              localTopic.agendaGroup.name,
                            )}
                          </p>
                          <p className="">
                            Item:{" "}
                            {localTopic.meeting && (
                              <>
                                {getGroupForTitleId(
                                  localTopic.meeting.groupNumbering,
                                  localTopic.agendaGroup.id,
                                  localTopic.meeting.groupOrder,
                                  localTopic.meeting.childNumbering,
                                )}
                                {getItemNumberOnly(
                                  localTopic.meeting.allTopics,
                                  localTopic.meeting.itemNumbering,
                                  localTopic,
                                  localTopic.order - 1,
                                  localTopic.meeting.groupNumbering,
                                )}
                              </>
                            )}
                          </p>
                        </div>
                      </div>
                      {localTopic.votedByBoard && (
                        <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">
                              Voted By Board
                            </p>
                          </div>
                          <div className="flex items-center justify-evenly">
                            <p className="">{localTopic.votedByBoard?.name}</p>
                          </div>
                        </div>
                      )}
                      {/* File and Presenter */}
                      {platform?.fieldReferenceFile && (
                        <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">
                              Reference File
                            </p>
                          </div>
                          <div>
                            <div className="min-h-[12px]">
                              <p className="text-center">
                                {localTopic.referenceFile}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      {platform?.fieldPresentedBy && (
                        <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">
                              Presented By
                            </p>
                          </div>
                          <div className="">
                            <p className="text-center">
                              {localTopic.presentedBy}
                            </p>
                          </div>
                        </div>
                      )}
                      {/* Custom Fields */}
                      <CustomFieldPrint
                        client={client}
                        fields={localTopic.customFields}
                      />
                      {/* Community Goals */}
                      {platform?.enablePriorities && (
                        <div className="mt-2 col-span-6 border border-gray-800 text-[12px]">
                          <div className="bg-gray-200 border-b border-gray-800 mb-1">
                            <p className="text-center font-medium">
                              Community Goals
                            </p>
                          </div>
                          <div className="px-3 py-2">
                            <div className="flex flex-wrap space-x-2">
                              {localTopic.priorities.map((item, index) => {
                                if (item.checked) {
                                  return (
                                    <li
                                      key={index}
                                      className="flex mt-0.5 items-center pl-2"
                                    >
                                      <p className="font-medium">
                                        &#9745; {item.name}
                                      </p>
                                    </li>
                                  );
                                } else return null;
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Details */}
                      <div className="py-1 col-span-6">
                        <EditorReadOnly
                          value={localTopic.details}
                          docId={localTopic.id}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="overflow-y-auto pb-sm">
                      {localTopic.useBuiltInHeader && (
                        <div className="grid grid-cols-6 px-4 bg-white rounded-t-md pb-6 2xl:max-w-7xl 2xl:mx-auto">
                          <div className="relative col-span-2 overflow-hidden min-h-[7em]">
                            <img
                              className="absolute object-contain h-full w-full p-4"
                              src={platform.logo}
                              alt=""
                            />
                          </div>
                          <div className="col-span-4 border-b border-black mt-2">
                            <div className="h-full w-full overflow-hidden grid grid-rows-2">
                              <div className="flex justify-end items-end font-semibold text-xl">
                                <h3 className="">{platform.clientName}</h3>
                              </div>
                              <div className="flex justify-end items-center font-semibold text-lg">
                                <h3 className="text-right">
                                  {localTopic.department?.name}{" "}
                                  {platform.memoCalled}
                                </h3>
                              </div>
                            </div>
                          </div>
                          {/* Agenda Title */}
                          <div className="mt-4 col-span-6 border-b border-gray-800 text-[12px]">
                            <p className="font-semibold 2xl:text-lg">
                              {localTopic.title}
                            </p>
                            <p className="whitespace-pre-line">
                              {localTopic.topicBlurb}
                            </p>
                            {localTopic.subTopics?.length > 0 &&
                              localTopic.subTopics.map((sub, sIdx) => {
                                return (
                                  <p
                                    key={`sub-${sIdx}`}
                                    className="ml-8 py-0.5"
                                  >
                                    &#8212; {sub}
                                  </p>
                                );
                              })}
                          </div>
                          {/* General Info */}
                          <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">Meeting</p>
                            </div>
                            <div className="">
                              <p className="text-center">
                                {localTopic.meeting?.scheduleDate}
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                            <div className="bg-gray-200 border-b border-gray-800 mb-1">
                              <p className="text-center font-medium">
                                Agenda Group
                              </p>
                            </div>
                            <div className="flex items-center justify-evenly">
                              {localTopic.meeting ? (
                                <>
                                  <p className="">
                                    {getNameByGroupId(
                                      localTopic.agendaGroup.id,
                                      localTopic.meeting.groupSubheadings,
                                      localTopic.agendaGroup.name,
                                    )}
                                  </p>
                                  <p className="">
                                    Item:{" "}
                                    {getGroupForTitleId(
                                      localTopic.meeting.groupNumbering,
                                      localTopic.agendaGroup.id,
                                      localTopic.meeting.groupOrder,
                                      localTopic.meeting.childNumbering,
                                    )}
                                    {getItemNumberOnly(
                                      localTopic.meeting.allTopics,
                                      localTopic.meeting.itemNumbering,
                                      localTopic,
                                      localTopic.order - 1,
                                      localTopic.meeting.groupNumbering,
                                    )}
                                  </p>
                                </>
                              ) : (
                                <>
                                  <p className="">
                                    {localTopic.agendaGroup?.parentGroup?.name
                                      ? `${localTopic.agendaGroup.parentGroup.name} - ${localTopic.agendaGroup.name}`
                                      : localTopic.agendaGroup?.name}
                                  </p>
                                  <p className="">Item: N/A</p>
                                </>
                              )}
                            </div>
                          </div>
                          {/* General Info */}
                          {localTopic.votedByBoard && (
                            <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Voted By Board
                                </p>
                              </div>
                              <div className="flex items-center justify-evenly">
                                <p className="">
                                  {localTopic.votedByBoard?.name}
                                </p>
                              </div>
                            </div>
                          )}
                          {/* File and Presenter */}
                          {platform?.fieldReferenceFile && (
                            <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Reference File
                                </p>
                              </div>
                              <div>
                                <div className="min-h-[12px]">
                                  <p className="text-center">
                                    {localTopic.referenceFile}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {platform?.fieldPresentedBy && (
                            <div className="mt-2 col-span-3 border border-gray-800 pb-1 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Presented By
                                </p>
                              </div>
                              <div className="">
                                <p className="text-center">
                                  {localTopic.presentedBy}
                                </p>
                              </div>
                            </div>
                          )}
                          {/* Custom Fields */}
                          <CustomFieldPrint
                            client={localTopic.clientId}
                            fields={localTopic.customFields}
                          />
                          {/* Community Goals */}
                          {platform?.enablePriorities && (
                            <div className="mt-2 col-span-6 border border-gray-800 text-[12px]">
                              <div className="bg-gray-200 border-b border-gray-800 mb-1">
                                <p className="text-center font-medium">
                                  Community Goals
                                </p>
                              </div>
                              <div className="px-3 py-2">
                                <div className="flex flex-wrap space-x-2">
                                  {localTopic.priorities.map((item, index) => {
                                    if (item.checked) {
                                      return (
                                        <li
                                          key={index}
                                          className="flex mt-0.5 items-center pl-2"
                                        >
                                          <p className="font-medium">
                                            &#9745; {item.name}
                                          </p>
                                        </li>
                                      );
                                    } else return null;
                                  })}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      <div
                        className={`relative mb-4 px-4 2xl:px-10 mt-2 2xl:mt-6 ${styles.tinyContainer}`}
                      >
                        {textToHtmlTinyMCE(localTopic.details)}
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* Minutes */}
              {activeToc?.fileType === "minutes" && localTopic && (
                <div className="mt-4 overflow-y-auto pb-sm">
                  {localTopic?.minutesInfo?.length > 0 && (
                    <>
                      {localTopic.minutesEditor === 1 && (
                        <EditorHTML value={localTopic.minutesInfo} />
                      )}
                      {localTopic.minutesEditor === 2 && (
                        <div
                          className={`mb-4 px-4 2xl:px-10 mt-2 2xl:mt-6 ${styles.tinyContainer}`}
                        >
                          {textToHtmlTinyMCE(localTopic.minutesInfo)}
                        </div>
                      )}
                    </>
                  )}
                  {/* Motions */}
                  {localTopic.motions?.length > 0 && localTopic.hasDetails && (
                    <div className="sm:space-y-2 flex flex-col mb-4 mt-4 px-4">
                      <p className="font-semibold">Motions</p>
                      <div>
                        {localTopic.motions.map((motion, index) => (
                          <div className="flex flex-col space-y-1" key={index}>
                            <p className="font-medium text-base">
                              {index + 1}. Motion Type:{" "}
                              {motion.motionType?.name}
                            </p>
                            <p className="pl-4 font-medium">
                              Motion By: {motion.motionedBy.name}
                            </p>
                            <p className="pl-4 font-medium">
                              Seconded By: {motion.secondedBy?.name}
                            </p>
                            {motion.wording?.length > 0 && (
                              <p className="pl-4 leading-none italic border-t border-zinc-700 pt-1">
                                {motion.wording}
                              </p>
                            )}
                            <p className="pl-4">
                              Vote Result:{" "}
                              {motion.votingResult === true
                                ? "Passed"
                                : "Failed"}
                            </p>
                            {localTopic.votingOption !== "RANKED" ? (
                              <>
                                {/* <p className="pl-4">Votes:</p> */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-1">
                                  {!motion.secretBallot &&
                                    motion.votes.map((vote, index) => {
                                      if (vote.counts !== "recuse") {
                                        return (
                                          <div
                                            key={vote._id}
                                            className={clsx(
                                              vote.counts === "positive"
                                                ? "bg-green-700"
                                                : "",
                                              vote.counts === "negative"
                                                ? "bg-red-700"
                                                : "",
                                              vote.counts === "no-vote"
                                                ? "bg-zinc-700"
                                                : "",
                                              vote.counts === "recuse"
                                                ? "bg-zinc-700"
                                                : "",
                                              vote.counts === "absent"
                                                ? "bg-zinc-700"
                                                : "",
                                              "relative rounded-lg pr-2 flex items-center space-x-2",
                                            )}
                                          >
                                            <div className="flex-shrink-0">
                                              <img
                                                className="h-10 w-10 rounded-l-md"
                                                src={vote.member.photoUrl}
                                                alt=""
                                              />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="focus:outline-none">
                                                <span className="absolute inset-0" />
                                                <p className="text-xs lg:text-sm 2xl:text-base text-zinc-200">
                                                  {vote.member.name}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      } else {
                                        return null;
                                      }
                                    })}
                                </div>

                                {motion.secretBallot && (
                                  <TallyResults votes={motion.votes} />
                                )}
                              </>
                            ) : (
                              <>
                                <p className="pl-4">
                                  Ranked Vote with {ranked?.openings} spots
                                  filled:
                                </p>
                                {ranked?.approved.map((vote, index) => (
                                  <p
                                    className="pl-8 font-medium py-0"
                                    key={index * 300}
                                  >
                                    {vote}
                                  </p>
                                ))}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* Attachments */}
              {activeToc?.fileType === "pdf" &&
                !activeToc?.docAccess?.enabled && (
                  <div className="flex-1 flex flex-col overflow-hidden px-2 min-h-0 pb-32">
                    <div className="flex-1 overflow-hidden">
                      <div className="h-full w-full">
                        <AdobeViewer
                          url={activeToc?.fileUrl}
                          fileName={activeToc?.fileName}
                        />
                      </div>
                    </div>
                  </div>
                )}
              {activeToc?.fileType === "pdf" &&
                activeToc?.docAccess?.enabled &&
                activeToc?.docAccess?.accessUrl && (
                  <div className="flex-1 flex flex-col overflow-hidden px-2 min-h-0 pb-32">
                    <div className="flex-1 overflow-hidden flex flex-col">
                      <div className="flex justify-start px-2 py-1 flex-shrink-0">
                        <a
                          href={activeToc.docAccess.accessUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm 2xl:text-base text-blue-600 hover:text-blue-800 underline"
                        >
                          Open in new tab
                        </a>
                      </div>
                      <iframe
                        src={activeToc.docAccess?.accessUrl}
                        className="h-full w-full border-0"
                        title="Document Viewer"
                        allow="fullscreen"
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgendaTopicPlayerToc;
